// The client side of /api/signals (BUILD-BRIEF-R2 §8) — owned by the `signals` agent.
import { qs, request } from './client.js';
import type {
  ListResponse, ScoutHealth, Signal, SignalDisposition, SignalKind, SignalSeverity, SignalStatus,
} from '../../shared/types.js';

export function listSignals(
  params: { status?: SignalStatus; kind?: SignalKind; since?: string; subject_kind?: string; subject_id?: number; limit?: number } = {},
): Promise<ListResponse<Signal>> {
  return request(`/signals${qs(params)}`);
}

export function getSignal(id: number): Promise<Signal> {
  return request(`/signals/${id}`);
}

export function markSeen(id: number): Promise<Signal> {
  return request(`/signals/${id}/seen`, { method: 'POST' });
}

/** task → a commitment the operator's click wrote · draft → a `draft` proposal · dismissed → nothing.
 *  All three set `disposition_ref`, so a signal can never be acted on twice. */
export function dispose(id: number, disposition: SignalDisposition, detail: Record<string, unknown> = {}): Promise<Signal> {
  return request(`/signals/${id}/dispose`, { method: 'POST', body: JSON.stringify({ disposition, ...detail }) });
}

/**
 * GET /api/signals/summary — one round trip for the Signals rail AND the Organisations board's
 * SIGNALS cell and "With a signal" chip.
 *
 * `linkedin_posts` is `null` when no `linkedin_manual` monitor exists: nobody collected it, and
 * null is not zero. `scout` is `null` when the scout's own service cannot answer — the worker is
 * a separate process and is only ever seen THROUGH the queue, never through a self-report.
 */
export interface SignalsSummary {
  new: number;
  new_by_kind: Array<{ kind: SignalKind; count: number }>;
  new_by_severity: Record<SignalSeverity, number>;
  last_7d: number;
  acted_7d: number;
  open_7d: number;
  monitors_total: number;
  monitors_by_kind: Array<{ kind: string; count: number }>;
  monitors_failed: number;
  monitors_organisations: number;
  organisation_ids_7d: number[];
  organisation_ids_open: number[];
  linkedin_posts: number | null;
  runs_24h: number;
  refused_24h: number;
  scout: (ScoutHealth & { fresh?: boolean }) | null;
}

export function signalsSummary(): Promise<SignalsSummary> {
  return request('/signals/summary');
}
